/**
 * Merge-tag helpers shared by every composer (Campaign editor email + SMS,
 * Find Buyers email + SMS, the template editor).
 *
 * Monday 12918907065. The vocabulary itself is NOT defined here - it comes from
 * `GET /communications/merge-tags/` (backend `services/merge_tags.py`), because
 * the bug this whole change exists to fix was two lists disagreeing:
 * CommunicationsPage advertised `["FirstName","Name","Suburb","State"]` while a
 * template could reference {{PropertyAddress}}, and the send engine replaced any
 * tag it didn't know with an empty string. Real buyers would have received
 * "just a reminder that  goes to auction  at ."
 *
 * Two scopes, mirroring the backend:
 *   - recipient: filled per person AT SEND TIME. Leave these alone.
 *   - compose:   the same for the whole send (property / agent / office). Filled
 *                HERE, so the agent sees the real text and can fix it.
 */

export const TAG_RE = /{{\s*([a-zA-Z0-9_]+)\s*}}/g;

/** Every {{tag}} used in the given strings, canonicalised through `aliases`. */
export function findTags(vocab, ...texts) {
  const aliases = vocab?.aliases || {};
  const found = new Set();
  texts.forEach((text) => {
    if (!text) return;
    // A fresh regex per pass: TAG_RE is global, so a shared instance carries
    // lastIndex between calls and silently skips matches on the second string.
    const re = new RegExp(TAG_RE.source, "g");
    let m;
    while ((m = re.exec(text)) !== null) found.add(aliases[m[1]] || m[1]);
  });
  return found;
}

/** Just the compose-scope tags - what the composer still has to fill in. */
export function composeScopeTags(vocab, ...texts) {
  const byName = tagsByName(vocab);
  return new Set(
    [...findTags(vocab, ...texts)].filter((name) => byName[name]?.scope === "compose")
  );
}

export function tagsByName(vocab) {
  const out = {};
  (vocab?.tags || []).forEach((t) => {
    out[t.name] = t;
  });
  return out;
}

/**
 * Fill in the tags `ctx` has values for, and LEAVE the rest in place.
 *
 * Deliberately different from the send-time pass, which blanks what it doesn't
 * know: while the body is still being edited an unfilled {{PropertyAddress}}
 * must stay visible, both so it survives until a property is picked and so
 * `unresolvedTags` can flag it.
 */
export function substituteTags(text, ctx = {}, vocab = null) {
  if (!text) return text || "";
  const aliases = vocab?.aliases || {};
  return text.replace(new RegExp(TAG_RE.source, "g"), (whole, raw) => {
    const value = ctx[aliases[raw] || raw];
    return value === undefined || value === null || value === "" ? whole : String(value);
  });
}

/**
 * Compose-scope tags in `text` that `ctx` cannot fill, worst-first.
 * Recipient-scope tags are never reported - those are correct to still be there.
 *
 * `notes` replaces the generic reason for a named tag with what the backend
 * actually knows. "Pick a property to fill this in" is wrong and unhelpful when
 * a property IS picked and the real answer is that this listing has no page on
 * the buyer portal (CRM-2026-00081).
 */
export function unresolvedTags(vocab, text, ctx = {}, notes = {}) {
  // FAIL OPEN when the vocabulary hasn't loaded. Without this guard an empty
  // vocab makes every tag "unknown" - including {{FirstName}} - so a perfectly
  // good message would raise a scary warning on every send whenever the
  // /merge-tags/ fetch was slow or down. A guard that cries wolf gets clicked
  // through, which is worse than no guard at all.
  if (!vocab?.tags?.length) return [];
  const byName = tagsByName(vocab);
  const notYet = {};
  (vocab?.not_yet_available || []).forEach((n) => {
    notYet[n.name] = n.reason;
  });

  const rows = [];
  [...composeScopeTags(vocab, text)].sort().forEach((name) => {
    if (ctx[name]) return;
    const tag = byName[name] || {};
    rows.push({
      name,
      label: tag.label || name,
      group: tag.group || "",
      reason: notes[name] || notYet[name] || missingReason(tag.group),
    });
  });
  // Tags the CRM has never heard of - a typo, or a placeholder invented in a
  // brief. These are the silent ones, so they must surface too.
  [...findTags(vocab, text)]
    .filter((name) => !byName[name])
    .sort()
    .forEach((name) => {
      rows.push({
        name,
        label: name,
        group: "",
        reason: notYet[name] || "Not a merge tag the CRM knows.",
      });
    });
  return rows;
}

function missingReason(group) {
  if (group === "Property") return "Pick a property to fill this in.";
  if (group === "Agent") return "Pick the agent to contact to fill this in.";
  return "No value available.";
}

/**
 * Compose-time context from what the composer already has on screen.
 *
 * Only the AGENT and OFFICE parts are derivable in the browser; property values
 * (price label, auction time, next open home) need the listing, so the caller
 * merges in `resolved` from `checkMergeTags` / the property picker payload.
 */
export function agentContext(agent) {
  if (!agent) return {};
  const full = (agent.name || agent.email || "").trim();
  const ctx = {};
  if (full) {
    ctx.AgentFullName = full;
    ctx.AgentFirstName = full.split(" ")[0];
  }
  const mobile = (agent.phone_number || agent.phone || "").trim();
  if (mobile) ctx.AgentMobile = mobile;
  if (agent.email) ctx.AgentEmail = agent.email;
  return ctx;
}

/** A one-line human summary for the pre-send warning dialog. */
export function unresolvedSummary(rows) {
  if (!rows.length) return "";
  const names = rows.map((r) => r.label).join(", ");
  return rows.length === 1
    ? `${names} has no value and would send as a blank gap.`
    : `${names} have no values and would send as blank gaps.`;
}
